import React, { useState, useMemo } from 'react';
import {
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '../../../../../utils/FormatFunctions';
import {
  IResumoTipo,
  IResumoAcumulador,
  IResumoDivergencias,
} from '../../../../../hooks/useFiscalContabil';
import { TIPO_HEX_COLORS, TIPO_HEX_COLORS_LIGHT, safeNumber } from '../constants';

interface FiscalContabilChartsProps {
  resumoTipo: IResumoTipo[];
  acumuladores: IResumoAcumulador[];
  divergencias: IResumoDivergencias | undefined;
  isLoading: boolean;
}

type ViewType = 'distribuicao' | 'acumuladores';
type ChartType = 'pie' | 'list';
type ViewMode = 'quantidade' | 'valor' | 'ambos';

interface DivergenciaItem {
  name: string;
  tipo: number;
  value: number;
  quantidade: number;
  valorTotal: number;
  color: string;
  isDivergent: boolean;
  ring: 'inner';
}

interface OuterRingItem {
  name: string;
  tipo: number;
  value: number;
  valorTotal: number;
  color: string;
  colorLight?: string;
  chartValue?: number;
  ring: 'outer';
}

type PieTooltipItem = DivergenciaItem | OuterRingItem;

// Cores hex e light agora vêm de constants para única fonte de verdade.
const TIPO_COLORS = TIPO_HEX_COLORS;
const TIPO_COLORS_LIGHT = TIPO_HEX_COLORS_LIGHT;

const CHART_COLORS = [
  '#1976D2',
  '#4CAF50',
  '#F57C00',
  '#E91E63',
  '#9C27B0',
  '#00ACC1',
  '#8BC34A',
  '#FF9800',
  '#F44336',
  '#673AB7',
];

interface DistribuicaoTooltipProps {
  active?: boolean;
  payload?: Array<{ payload: PieTooltipItem }>;
  viewMode: ViewMode;
  chartTotal: number;
}

function DistribuicaoTooltip({ active, payload, viewMode, chartTotal }: DistribuicaoTooltipProps) {
  if (!active || !payload?.length) return null;
  const data = payload[0].payload;

  if (data.ring === 'inner') {
    const percent = chartTotal > 0 ? ((data.value / chartTotal) * 100).toFixed(1) : '0';
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
        <p className="font-semibold text-gray-800">{data.name}</p>
        <p className="text-sm text-gray-600">
          {data.quantidade} registros ({percent}%)
        </p>
        <p className="text-sm text-gray-500">{formatCurrency(data.valorTotal)}</p>
      </div>
    );
  }

  const chartValue = data.chartValue ?? data.value ?? 0;
  const valorTotal = data.valorTotal ?? 0;
  const value = data.value ?? 0;
  const percent = chartTotal > 0 ? ((chartValue / chartTotal) * 100).toFixed(1) : '0';

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-3 shadow-lg">
      <p className="font-semibold text-gray-800">{data.name}</p>
      {viewMode === 'quantidade' ? (
        <>
          <p className="text-sm text-gray-600">
            {value} registros ({percent}%)
          </p>
          <p className="text-sm text-gray-500">{formatCurrency(valorTotal)}</p>
        </>
      ) : (
        <>
          <p className="text-sm text-gray-600">
            {formatCurrency(valorTotal)} ({percent}%)
          </p>
          <p className="text-sm text-gray-500">{value} registros</p>
        </>
      )}
    </div>
  );
}

export function FiscalContabilCharts({
  resumoTipo,
  acumuladores,
  divergencias,
  isLoading,
}: FiscalContabilChartsProps) {
  const [currentView, setCurrentView] = useState<ViewType>('distribuicao');
  const [chartType, setChartType] = useState<ChartType>('pie');
  const [viewMode, setViewMode] = useState<ViewMode>('ambos');

  // Dados para o donut externo (totais por tipo)
  const tipoData = useMemo<OuterRingItem[]>(() => {
    const tipoMap: Record<number, IResumoTipo> = {};
    resumoTipo.forEach(item => {
      tipoMap[item.tipo] = item;
    });

    const items: OuterRingItem[] = [
      {
        tipo: 1,
        name: 'Entradas',
        value: tipoMap[1]?.quantidade ?? 0,
        valorTotal: safeNumber(tipoMap[1]?.valor_total),
        color: TIPO_COLORS[1],
        colorLight: TIPO_COLORS_LIGHT[1],
        ring: 'outer',
      },
      {
        tipo: 2,
        name: 'Saídas',
        value: tipoMap[2]?.quantidade ?? 0,
        valorTotal: safeNumber(tipoMap[2]?.valor_total),
        color: TIPO_COLORS[2],
        colorLight: TIPO_COLORS_LIGHT[2],
        ring: 'outer',
      },
      {
        tipo: 3,
        name: 'Serviços',
        value: tipoMap[3]?.quantidade ?? 0,
        valorTotal: safeNumber(tipoMap[3]?.valor_total),
        color: TIPO_COLORS[3],
        colorLight: TIPO_COLORS_LIGHT[3],
        ring: 'outer',
      },
    ];
    return items.filter(item => item.value > 0);
  }, [resumoTipo]);

  const truncate = (text: string, max = 25) =>
    text.length > max ? `${text.substring(0, max)}...` : text;

  const acumuladoresData = useMemo(() => {
    return acumuladores.slice(0, 10).map((item, index) => {
      const fullName = item.acumulador_nome || `Acum. ${item.acumulador}`;
      return {
        name: truncate(fullName),
        fullName,
        value: item.quantidade,
        valorTotal: safeNumber(item.valor_total),
        color: CHART_COLORS[index % CHART_COLORS.length],
      };
    });
  }, [acumuladores]);

  const totais = useMemo(() => {
    const total = tipoData.reduce((sum, item) => sum + item.value, 0);
    const totalDivergencias = divergencias?.total ?? 0;
    const valorTotal = tipoData.reduce((sum, item) => sum + item.valorTotal, 0);
    const valorDivergencias = safeNumber(divergencias?.diferenca_total);
    return { total, totalDivergencias, valorTotal, valorDivergencias };
  }, [tipoData, divergencias]);

  // Dados para o gráfico baseado no viewMode
  const chartData = useMemo(() => {
    return tipoData.map(item => ({
      ...item,
      chartValue: viewMode === 'quantidade' ? item.value : item.valorTotal,
    }));
  }, [tipoData, viewMode]);

  // Dados de divergências para o gráfico interno baseado no viewMode
  const divergenciasChartData = useMemo((): DivergenciaItem[] => {
    if (!divergencias) return [];

    const result: DivergenciaItem[] = [];

    const tipoMap: Record<number, { quantidade: number; valor: number }> = {};
    resumoTipo.forEach(item => {
      tipoMap[item.tipo] = {
        quantidade: item.quantidade,
        valor: safeNumber(item.valor_total),
      };
    });

    const { por_tipo: porTipo } = divergencias;
    const tipos = [
      { tipo: 1, name: 'Entradas', divCount: porTipo?.entradas ?? 0 },
      { tipo: 2, name: 'Saídas', divCount: porTipo?.saidas ?? 0 },
      { tipo: 3, name: 'Serviços', divCount: porTipo?.servicos ?? 0 },
    ];

    // Valor divergente por tipo vem do backend (SUM real). Antes era estimativa
    // proporcional (qtdDiv/qtdTotal*valor) — falhava em datasets concentrados.
    const valorPorTipo = divergencias?.valor_por_tipo;
    const valorDivergPorTipo = (tipo: number): number => {
      if (!valorPorTipo) return 0;
      if (tipo === 1) return valorPorTipo.entradas;
      if (tipo === 2) return valorPorTipo.saidas;
      if (tipo === 3) return valorPorTipo.servicos;
      return 0;
    };

    tipos.forEach(t => {
      const tipoInfo = tipoMap[t.tipo];
      if (!tipoInfo) return;

      const total = viewMode === 'quantidade' ? tipoInfo.quantidade : tipoInfo.valor;
      if (total <= 0) return;

      // divCount nunca pode exceder a quantidade total — clamp defensivo.
      const safeDivCount = Math.min(Math.max(0, t.divCount), tipoInfo.quantidade);
      const divValorReal = valorDivergPorTipo(t.tipo);
      const divValue = viewMode === 'quantidade' ? safeDivCount : divValorReal;
      const okQtd = Math.max(0, tipoInfo.quantidade - safeDivCount);
      const okValor = Math.max(0, tipoInfo.valor - divValorReal);
      const okValueChart = Math.max(0, total - divValue);

      result.push({
        name: `${t.name} (Div.)`,
        tipo: t.tipo,
        value: divValue,
        quantidade: safeDivCount,
        valorTotal: divValorReal,
        ring: 'inner',
        color: '#E53E3E',
        isDivergent: true,
      });
      result.push({
        name: `${t.name} (OK)`,
        tipo: t.tipo,
        value: okValueChart,
        quantidade: okQtd,
        valorTotal: okValor,
        ring: 'inner',
        color: TIPO_COLORS_LIGHT[t.tipo],
        isDivergent: false,
      });
    });

    return result;
  }, [divergencias, resumoTipo, viewMode]);

  const toggleChartView = () => {
    setChartType(chartType === 'pie' ? 'list' : 'pie');
  };

  const getDivergenciasByTipo = (tipo: number): number => {
    if (!divergencias) return 0;
    switch (tipo) {
      case 1:
        return divergencias.por_tipo.entradas;
      case 2:
        return divergencias.por_tipo.saidas;
      case 3:
        return divergencias.por_tipo.servicos;
      default:
        return 0;
    }
  };

  // Valor REAL divergente por tipo, vindo do backend (SUM da |valor_contabil - valor|).
  // Antes usava estimativa proporcional `(divCount / qtdTotal) * valor`, que falhava
  // em datasets concentrados (1 nota gigante divergente entre 99 pequenas).
  const getDivergenciasValueByTipo = (tipo: number): number => {
    const map = divergencias?.valor_por_tipo;
    if (!map) return 0;
    if (tipo === 1) return map.entradas;
    if (tipo === 2) return map.saidas;
    if (tipo === 3) return map.servicos;
    return 0;
  };

  const chartTotal = viewMode === 'quantidade' ? totais.total : totais.valorTotal;

  const renderDistribuicao = () => {
    if (tipoData.length === 0) {
      return (
        <div className="flex h-full items-center justify-center text-gray-500">
          Sem dados disponíveis
        </div>
      );
    }

    const formatValue = (val: number) => {
      if (viewMode === 'valor') return formatCurrency(val);
      return val.toString();
    };

    if (chartType === 'list') {
      return (
        <div className="space-y-3">
          {/* Resumo geral */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-lg bg-blue-50 p-2 text-center">
              <p className="text-xs text-gray-500">Total</p>
              <p className="text-lg font-bold text-blue-700">
                {viewMode === 'quantidade' ? totais.total : formatCurrency(totais.valorTotal)}
              </p>
              <p className="text-[10px] text-gray-400">
                {viewMode === 'quantidade'
                  ? formatCurrency(totais.valorTotal)
                  : `${totais.total} reg.`}
              </p>
            </div>
            <div
              className={`rounded-lg p-2 text-center ${
                totais.totalDivergencias > 0 ? 'bg-red-50' : 'bg-green-50'
              }`}
            >
              <p className="text-xs text-gray-500">Divergências</p>
              <p
                className={`text-lg font-bold ${
                  totais.totalDivergencias > 0 ? 'text-red-600' : 'text-green-600'
                }`}
              >
                {viewMode === 'quantidade'
                  ? totais.totalDivergencias
                  : formatCurrency(totais.valorDivergencias)}
              </p>
              <p className="text-[10px] text-gray-400">
                {viewMode === 'quantidade'
                  ? `${totais.total > 0 ? ((totais.totalDivergencias / totais.total) * 100).toFixed(1) : '0'}%`
                  : `${totais.totalDivergencias} reg.`}
              </p>
            </div>
          </div>

          {/* Lista por tipo */}
          <div className="space-y-2">
            {tipoData.map(item => {
              const divCount = getDivergenciasByTipo(item.tipo);
              const okCount = item.value - divCount;
              const divPercent = item.value > 0 ? ((divCount / item.value) * 100).toFixed(1) : '0';
              const displayValue = viewMode === 'quantidade' ? item.value : item.valorTotal;

              return (
                <div key={item.tipo} className="rounded-lg border border-gray-100 bg-gray-50 p-2">
                  <div className="mb-1 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div
                        className="h-3 w-3 rounded-full"
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm font-medium">{item.name}</span>
                    </div>
                    <span className="text-sm font-bold">{formatValue(displayValue)}</span>
                  </div>

                  {/* Barra de progresso mostrando divergências */}
                  <div className="mb-1 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                    <div className="flex h-full">
                      <div
                        className="h-full"
                        style={{
                          width: `${item.value > 0 ? (okCount / item.value) * 100 : 0}%`,
                          backgroundColor: item.color,
                        }}
                      />
                      {divCount > 0 && (
                        <div
                          className="h-full bg-red-500"
                          style={{
                            width: `${item.value > 0 ? (divCount / item.value) * 100 : 0}%`,
                          }}
                        />
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between text-[10px]">
                    <span className="text-gray-500">
                      {viewMode === 'quantidade'
                        ? formatCurrency(item.valorTotal)
                        : `${item.value} reg.`}
                    </span>
                    {divCount > 0 && (
                      <span className="text-red-600">
                        {viewMode === 'quantidade'
                          ? `${divCount} div. (${divPercent}%)`
                          : formatCurrency(
                              item.value > 0 ? (divCount / item.value) * item.valorTotal : 0,
                            )}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    // Versão combinada - mostra Qtd e Valor juntos
    if (viewMode === 'ambos') {
      return (
        <div className="flex h-full gap-3">
          {/* Legenda combinada */}
          <div className="flex w-2/5 flex-col justify-center gap-1.5">
            {/* Total */}
            <div className="rounded-lg bg-gray-50 p-2">
              <p className="mb-1 text-[10px] font-medium text-gray-500">TOTAL</p>
              <table className="w-full text-[10px]">
                <tbody>
                  <tr>
                    <td className="w-[70px] align-top">
                      <p className="text-lg font-bold text-gray-800">{totais.total}</p>
                      {totais.totalDivergencias > 0 && (
                        <p className="text-[9px] text-red-500">{totais.totalDivergencias} div.</p>
                      )}
                    </td>
                    <td className="text-right align-top">
                      <p className="font-semibold text-blue-600">
                        {formatCurrency(totais.valorTotal)}
                      </p>
                      {totais.totalDivergencias > 0 && (
                        <p className="text-[9px] text-red-500">
                          {formatCurrency(totais.valorDivergencias)}
                        </p>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Tipos */}
            {tipoData.map(item => {
              const divCount = getDivergenciasByTipo(item.tipo);
              const divValue = getDivergenciasValueByTipo(item.tipo);

              return (
                <div
                  key={item.tipo}
                  className="rounded bg-gray-50 px-2 py-1.5"
                  style={{ borderLeft: `3px solid ${item.color}` }}
                >
                  <table className="w-full text-[10px]">
                    <tbody>
                      <tr>
                        <td className="w-[65px] align-top font-semibold text-gray-700">
                          {item.name}
                        </td>
                        <td className="w-[55px] align-top">
                          <p className="font-bold">{item.value}</p>
                          {divCount > 0 && (
                            <p className="text-[9px] text-red-500">{divCount} div.</p>
                          )}
                        </td>
                        <td className="text-right align-top">
                          <p className="text-gray-600">{formatCurrency(item.valorTotal)}</p>
                          {divCount > 0 && (
                            <p className="text-[9px] text-red-500">{formatCurrency(divValue)}</p>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>

          {/* Gráfico de donut */}
          <div
            className="flex min-h-[220px] flex-1 items-center justify-center"
            role="img"
            aria-label="Gráfico de donut com distribuição de notas por tipo (entradas, saídas, serviços)"
          >
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={tipoData}
                  cx="50%"
                  cy="50%"
                  innerRadius={55}
                  outerRadius={80}
                  dataKey="value"
                  stroke="#fff"
                  strokeWidth={2}
                >
                  {tipoData.map(entry => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
                {divergenciasChartData.length > 0 && (
                  <Pie
                    data={divergenciasChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={35}
                    outerRadius={52}
                    dataKey="value"
                    stroke="#fff"
                    strokeWidth={1}
                  >
                    {divergenciasChartData.map(entry => (
                      <Cell
                        key={`inner-${entry.tipo}-${entry.isDivergent ? 'div' : 'ok'}`}
                        fill={entry.isDivergent ? '#E53E3E' : entry.color}
                        opacity={entry.isDivergent ? 1 : 0.4}
                      />
                    ))}
                  </Pie>
                )}
                <Tooltip
                  content={<DistribuicaoTooltip viewMode={viewMode} chartTotal={chartTotal} />}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      );
    }

    // Versão compacta para telas menores - toggle entre Qtd/Valor
    return (
      <div className="flex h-full gap-2">
        {/* Legenda e estatísticas */}
        <div className="flex w-1/3 flex-col justify-center gap-2">
          {/* Total central */}
          <div className="mb-2 rounded-lg bg-gray-50 p-2 text-center">
            <p className="text-[10px] text-gray-500">TOTAL</p>
            <p className="text-xl font-bold text-gray-800">
              {viewMode === 'quantidade' ? totais.total : formatCurrency(totais.valorTotal)}
            </p>
            {totais.totalDivergencias > 0 && (
              <p className="text-[10px] font-medium text-red-600">
                {viewMode === 'quantidade'
                  ? `${totais.totalDivergencias} div. (${((totais.totalDivergencias / totais.total) * 100).toFixed(1)}%)`
                  : formatCurrency(totais.valorDivergencias)}
              </p>
            )}
          </div>

          {/* Legenda por tipo */}
          {tipoData.map(item => {
            const divCount = getDivergenciasByTipo(item.tipo);
            const divValue = getDivergenciasValueByTipo(item.tipo);

            return (
              <div key={item.tipo} className="rounded bg-gray-50 px-2 py-1 text-[10px]">
                <div className="flex items-center gap-1">
                  <div className="h-2 w-2 rounded-full" style={{ backgroundColor: item.color }} />
                  <span className="font-medium">{item.name}:</span>
                  <span className="font-bold">
                    {viewMode === 'quantidade' ? item.value : formatCurrency(item.valorTotal)}
                  </span>
                </div>
                {divCount > 0 && (
                  <div className="ml-3 flex items-center gap-1 text-red-600">
                    <div className="h-1.5 w-1.5 rounded-full bg-red-500" />
                    <span>
                      {viewMode === 'quantidade' ? `${divCount} div.` : formatCurrency(divValue)}
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Gráfico de donut aninhado */}
        <div
          className="flex-1"
          role="img"
          aria-label="Gráfico de donut aninhado: anel externo com totais por tipo, anel interno com proporção de divergências"
        >
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              {/* Donut externo - Totais por tipo */}
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={80}
                dataKey="chartValue"
                stroke="#fff"
                strokeWidth={2}
              >
                {chartData.map(entry => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>

              {/* Donut interno - Divergências */}
              {divergenciasChartData.length > 0 && (
                <Pie
                  data={divergenciasChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={35}
                  outerRadius={52}
                  dataKey="value"
                  stroke="#fff"
                  strokeWidth={1}
                >
                  {divergenciasChartData.map(entry => (
                    <Cell
                      key={`inner-${entry.tipo}-${entry.isDivergent ? 'div' : 'ok'}`}
                      fill={entry.isDivergent ? '#E53E3E' : entry.color}
                      opacity={entry.isDivergent ? 1 : 0.4}
                    />
                  ))}
                </Pie>
              )}

              <Tooltip
                content={<DistribuicaoTooltip viewMode={viewMode} chartTotal={chartTotal} />}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  const renderAcumuladores = () => {
    if (acumuladoresData.length === 0) {
      return (
        <div className="flex h-full items-center justify-center text-gray-500">
          Sem dados de acumuladores
        </div>
      );
    }

    if (chartType === 'list') {
      return (
        <div className="h-[280px] overflow-y-auto">
          {acumuladores.map((item, idx) => (
            <div
              key={item.acumulador ?? 'null'}
              className="mb-1 flex items-center gap-2 rounded bg-gray-50 p-1.5 text-xs transition-colors hover:bg-blue-50"
              title={item.acumulador_nome || `Acum. ${item.acumulador}`}
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-500 text-[11px] font-bold text-white">
                {idx + 1}
              </span>
              <span className="flex-1 truncate font-medium text-gray-800">
                {item.acumulador_nome || `Acum. ${item.acumulador}`}
              </span>
              <div className="text-right">
                <span className="rounded-full bg-blue-500 px-2 py-0.5 text-[11px] font-semibold text-white">
                  {item.quantidade}
                </span>
                <p className="text-[9px] text-gray-400">
                  {formatCurrency(safeNumber(item.valor_total))}
                </p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div
        className="h-full w-full"
        role="img"
        aria-label="Gráfico de barras horizontais com valor total por acumulador"
      >
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={acumuladoresData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis type="number" tick={{ fontSize: 10 }} />
            <YAxis
              dataKey="name"
              type="category"
              width={100}
              tick={({ x, y, payload }) => {
                const item = acumuladoresData.find(a => a.name === payload.value);
                return (
                  <g transform={`translate(${x},${y})`}>
                    <title>{item?.fullName ?? payload.value}</title>
                    <text x={-4} y={0} dy={3} textAnchor="end" fontSize={9} fill="#6B7280">
                      {payload.value}
                    </text>
                  </g>
                );
              }}
              interval={0}
            />
            <Tooltip
              formatter={(
                value: number,
                _name: string,
                entry: { payload?: { valorTotal?: number; fullName?: string } },
              ) => [
                `${value} registros - ${formatCurrency(entry?.payload?.valorTotal || 0)}`,
                entry?.payload?.fullName || '',
              ]}
            />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {acumuladoresData.map(entry => (
                <Cell key={entry.fullName} fill={entry.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  };

  const renderChart = () => {
    if (isLoading) {
      return (
        <div className="flex h-full items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent" />
        </div>
      );
    }

    switch (currentView) {
      case 'distribuicao':
        return renderDistribuicao();
      case 'acumuladores':
        return renderAcumuladores();
      default:
        return null;
    }
  };

  return (
    <div className="flex h-full flex-col rounded-xl bg-white p-4 shadow-md">
      <div className="mb-2 flex flex-col gap-2 border-b border-gray-200 pb-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-1.5">
          <svg
            className="h-4 w-4 shrink-0 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => {
                setCurrentView('distribuicao');
                setChartType('pie');
              }}
              className={`rounded px-2 py-1 text-[10px] font-medium transition-colors sm:px-3 sm:text-xs ${
                currentView === 'distribuicao'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Distribuição
              {divergencias && divergencias.total > 0 && (
                <span className="ml-1 rounded-full bg-red-500 px-1.5 text-[9px] text-white">
                  {divergencias.total}
                </span>
              )}
            </button>
            <span className="hidden text-gray-400 sm:inline">|</span>
            <button
              type="button"
              onClick={() => {
                setCurrentView('acumuladores');
                setChartType('pie');
              }}
              className={`rounded px-2 py-1 text-[10px] font-medium transition-colors sm:px-3 sm:text-xs ${
                currentView === 'acumuladores'
                  ? 'bg-blue-500 text-white'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Acumuladores
              <span className="ml-1 text-[9px] text-gray-400">({acumuladores.length})</span>
            </button>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {currentView === 'distribuicao' && (
            <div className="flex rounded-lg border border-gray-200 p-0.5">
              <button
                type="button"
                onClick={() => setViewMode('ambos')}
                className={`rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                  viewMode === 'ambos'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Qtd+Valor
              </button>
              <button
                type="button"
                onClick={() => setViewMode('quantidade')}
                className={`rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                  viewMode === 'quantidade'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Qtd
              </button>
              <button
                type="button"
                onClick={() => setViewMode('valor')}
                className={`rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                  viewMode === 'valor'
                    ? 'bg-blue-500 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                Valor
              </button>
            </div>
          )}
          <button
            type="button"
            onClick={toggleChartView}
            className="shrink-0 rounded border border-gray-300 bg-white px-2 py-1.5 text-xs text-gray-600 transition-colors hover:border-blue-500 hover:bg-gray-50 hover:text-blue-500"
            title={chartType === 'pie' ? 'Ver como lista' : 'Ver como gráfico'}
          >
            {chartType === 'pie' ? (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            ) : (
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
      <div className="min-h-[300px] flex-1">{renderChart()}</div>
    </div>
  );
}
